import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import QRScanner from '../components/QRScanner';
import { redeemCustomerReward, getAllCustomerPasses } from '../services/scanApi';
import { createLogger } from '../../../shared/config/logger';
import { useScan } from '../hooks/useScan';
import { useAuth } from '../../auth/hooks/useAuth';
import { useAppSelector } from '../../../shared/store/hooks';
import { updateCustomerPointsThunk } from '../store/scanSlice';
import { selectSelectedPass, selectCustomerEmail, selectCustomerName } from '../store/scanSelectors';

const logger = createLogger('ScanPage');

export default function Scan() {
    const searchInputRef = useRef(null);
    const suggestionsContainerRef = useRef(null);
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        state: {
            error,
            loading,
            rewardsLoading,
            mode,
            message,
            email,
            customerData,
            customerLookupGeneration,
            availableRewards,
        },
        emailSearch,
        emailChange,
        qrSearch,
        getRewards,
        setMode,
        updatePassInRedux,
    } = useScan();
    useAuth(); // ensure auth state/hooks run but we don't need userId here

    // When opened with ?passId=... (e.g. from Customers "View pass"), load that pass
    useEffect(() => {
        const passIdFromUrl = searchParams.get('passId');
        if (passIdFromUrl) {
            qrSearch(passIdFromUrl).then(() => {
                setSearchParams({}, { replace: true });
            }).catch((err) => {
                logger.error('Failed to load pass from URL', { error: err, passId: passIdFromUrl });
                setSearchParams({}, { replace: true });
            });
        }
    }, [searchParams, qrSearch, setSearchParams]);

    // Use selectors for derived state
    const selectedPass = useAppSelector(selectSelectedPass);
    const customerEmail = useAppSelector(selectCustomerEmail);
    const customerName = useAppSelector(selectCustomerName);
    const rewardsettings = useAppSelector((state) => state.settings.rewardsettings);
    const pointRatio = rewardsettings?.point_per_dollar || 0;
    const [isScanEnabled, setIsScanEnabled] = useState(true);
    const [addVisit, setAddVisit] = useState(true);
    const [moneySpent, setMoneySpent] = useState('');
    const [showAddPointsConfirm, setShowAddPointsConfirm] = useState(false);
    const [allCustomersForSuggestions, setAllCustomersForSuggestions] = useState([]);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const handleEmailChange = (e) => {
        emailChange(e.target.value);
        setShowSuggestions(true);
    };

    const handleEmailSearch = async () => {
        try {
            setShowSuggestions(false);
            await emailSearch(email);
        } catch (error) {
            logger.error(error);
        }
    };

    const handleSuggestionClick = async (value) => {
        emailChange(value);
        setShowSuggestions(false);
        try {
            await emailSearch(value);
        } catch (error) {
            logger.error(error);
        }
    };

    const handleSetMode = (mode) => {
        try {
            setMode(mode);
        } catch (error) {
            logger.error(error);
        }
    };

    useEffect(() => {
        if ((mode === 'email' || mode === 'phone') && searchInputRef.current) {
            searchInputRef.current.focus(); // Auto-focus input
        }

        if (mode === 'qr') {
            setIsScanEnabled(true); // Auto-enable scanner
        }
    }, [mode]);

    useEffect(() => {
        let isMounted = true;
        const loadCustomersForSuggestions = async () => {
            try {
                const res = await getAllCustomerPasses();
                const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
                if (isMounted) {
                    setAllCustomersForSuggestions(rows);
                }
            } catch (err) {
                logger.error('Failed loading suggestions source', { error: err });
            }
        };
        loadCustomersForSuggestions();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const rawInput = (email || '').trim();
        if (!rawInput || !(mode === 'email' || mode === 'phone')) {
            setSearchSuggestions([]);
            return;
        }

        const lowered = rawInput.toLowerCase();
        const digitsInput = rawInput.replace(/\D/g, '');

        const suggestions = allCustomersForSuggestions
            .filter((row) => {
                if (mode === 'phone') {
                    const phone = row.customer_phone || row.phone || '';
                    const phoneDigits = phone.toString().replace(/\D/g, '');
                    return digitsInput.length > 0 && phoneDigits.includes(digitsInput);
                }
                const customerEmailValue = (row.customer_email || row.email || '').toLowerCase();
                return customerEmailValue.includes(lowered);
            })
            .slice(0, 8)
            .map((row) => ({
                name: row.customer_name || row.name || '',
                email: row.customer_email || row.email || '',
                phone: row.customer_phone || row.phone || '',
            }));

        // Deduplicate by the displayed value for active mode.
        const seen = new Set();
        const deduped = suggestions.filter((item) => {
            const key = mode === 'phone' ? item.phone : item.email;
            if (!key || seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });

        setSearchSuggestions(deduped);
    }, [email, mode, allCustomersForSuggestions]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (suggestionsContainerRef.current && !suggestionsContainerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    // Refetch when the selected pass changes or a new email/QR lookup completes (generation bumps).
    // Using customerLookupGeneration avoids refetch on points/visit-only updates to customerData.
    useEffect(() => {
        const selectedPassId = selectedPass?.passId;
        if (selectedPassId) {
            getRewards(selectedPassId);
        }
    }, [selectedPass?.passId, customerLookupGeneration, getRewards]);

    const handleQRScan = async (scannedId) => {
        if (!isScanEnabled) {return;}
        setIsScanEnabled(false); // Disable immediately to prevent spam

        try {
            await qrSearch(scannedId);
        } catch (error) {
            logger.error(error);
            // Optionally re-enable if failed
            setIsScanEnabled(true);
        }
    };

    const handleAddPoints = async () => {
        try {
            if (!selectedPass) {return;}
            const calculatedPoints = parseFloat((moneySpent || 0) * (pointRatio || 1));
            const res = await dispatch(
                updateCustomerPointsThunk({
                    passId: selectedPass.passId,
                    newPoints: calculatedPoints,
                    addVisit,
                    moneySpent,
                })
            ).unwrap();
            updatePassInRedux(res);
            setShowAddPointsConfirm(false);
        } catch (err) {
            logger.error('Error adding points or logging activity', { error: err });
            setShowAddPointsConfirm(false);
        }
    };

    const handleAddPointsClick = () => {
        setShowAddPointsConfirm(true);
    };

    const handleAddPointsConfirm = async () => {
        await handleAddPoints();
    };

    const handleRedeemReward = async (rewardId) => {
        try {
            if (!selectedPass || !customerEmail) {return;}
            const res = await redeemCustomerReward(
                customerEmail,
                rewardId,
                selectedPass.passId
            );

            // Update the pass in Redux with new points/visits
            updatePassInRedux({
                ...selectedPass,
                points: res.data.updatedPass.points,
                visits: res.data.updatedPass.visits,
            });

            // Refresh available rewards for the updated pass
            getRewards(selectedPass.passId);

            // Optionally, show a success message
            // setMessage(`🎉 Redeemed: ${res.data.reward.title}`);
        } catch (err) {
            // Log error for debugging and rethrow so callers can handle/fail-fast
            logger.error('Failed to redeem reward', err);
            throw err;
            // setMessage(err.response?.data?.error || '❌ Failed to redeem reward');
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {error && (
                <div className="mb-4 p-3 rounded bg-red-100 text-red-700 font-semibold border border-red-300">
                    {error}
                </div>
            )}
            {message && !error && (
                <div className="mb-4 p-3 rounded bg-green-100 text-green-700 font-semibold border border-green-300">
                    {message}
                </div>
            )}
            <h2 className="text-2xl font-bold text-primary mb-6">Scan or Search Customer</h2>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Left Panel: Search & Update */}
                <div className="w-full lg:w-1/2 bg-white shadow-md rounded-xl p-6 space-y-4">
                    <div className="flex gap-4">
                        <button
                            onClick={() => handleSetMode('qr')}
                            className={`px-4 py-2 rounded ${mode === 'qr' ? 'bg-primary text-white' : 'bg-gray-200'}`}
                            disabled={loading}
                        >
                            Scan QR Code
                        </button>
                        <button
                            onClick={() => handleSetMode('email')}
                            className={`px-4 py-2 rounded ${mode === 'email' ? 'bg-primary text-white' : 'bg-gray-200'}`}
                            disabled={loading}
                        >
                            Search by Email
                        </button>
                        <button
                            onClick={() => handleSetMode('phone')}
                            className={`px-4 py-2 rounded ${mode === 'phone' ? 'bg-primary text-white' : 'bg-gray-200'}`}
                            disabled={loading}
                        >
                            Search by Phone
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-3 border p-4 bg-gray-50 rounded">
                            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm text-gray-600">Searching customer...</p>
                        </div>
                    ) : (
                        <>
                    {/* QR Mode */}
                    {mode === 'qr' && (
                        <div className="border p-4 bg-gray-50 space-y-3">
                            <label className="block font-medium">Scan QR Code:</label>

                            {/* Scanner only active if scan is enabled */}
                            {isScanEnabled ? (
                                <QRScanner onScanSuccess={handleQRScan} />
                            ) : (
                                <p className="text-gray-500">
                                    ✅ Scan complete. Tap below to scan again.
                                </p>
                            )}

                            {/* Button to re-enable scanning */}
                            {!isScanEnabled && (
                                <button
                                    onClick={() => setIsScanEnabled(true)}
                                    className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                                    disabled={loading}
                                >
                                    Scan Again
                                </button>
                            )}
                        </div>
                    )}

                    {/* Email/Phone Mode */}
                    {(mode === 'email' || mode === 'phone') && (
                        <div className="space-y-3 border p-4 bg-gray-50 relative" ref={suggestionsContainerRef}>
                            <label className="block font-medium">
                                {mode === 'phone' ? 'Customer Phone:' : 'Customer Email:'}
                            </label>
                            <input
                                type="text"
                                value={email}
                                onChange={handleEmailChange}
                                className="border p-2 w-full"
                                placeholder={
                                    mode === 'phone'
                                        ? 'Enter customer phone'
                                        : 'Enter customer email'
                                }
                                ref={searchInputRef}
                                onFocus={() => setShowSuggestions(true)}
                            />
                            {showSuggestions && searchSuggestions.length > 0 && (
                                <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded shadow">
                                    {searchSuggestions.map((item, idx) => {
                                        const displayValue = mode === 'phone' ? item.phone : item.email;
                                        if (!displayValue) {
                                            return null;
                                        }
                                        return (
                                            <li key={`${displayValue}-${idx}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSuggestionClick(displayValue)}
                                                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                                                >
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">
                                                            {item.name || 'Unnamed customer'}
                                                        </span>
                                                        <span className="text-sm">{displayValue}</span>
                                                        {item.email && item.phone && (
                                                            <span className="text-xs text-gray-500">
                                                                {mode === 'phone' ? item.email : item.phone}
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            <button
                                onClick={handleEmailSearch}
                                className="bg-primary text-white px-4 py-2 rounded"
                                disabled={loading}
                            >
                                Search
                            </button>
                        </div>
                    )}

                    {/* Points Input + Submit */}
                    {customerData && customerEmail && (
                        <div className="border p-4 bg-green-50 space-y-2 rounded">
                            <label className="block font-medium">Money Spent ($):</label>
                            <input
                                type="number"
                                value={moneySpent}
                                onChange={(e) => {
                                    // Prevent negative values and invalid input
                                    const value = e.target.value;
                                    // Only allow valid positive numbers or empty string
                                    if (/^\d*\.?\d*$/.test(value)) {
                                        setMoneySpent(value);
                                    }
                                }}
                                step="0.01"
                                min="0"
                                className="border p-2 w-full"
                                placeholder="Enter money spent"
                            />

                            {/* Preview Points */}
                            <p className="text-sm text-gray-700">
                                💡 This will add{' '}
                                <strong>
                                    {((parseFloat(moneySpent) || 0) * (pointRatio || 1)).toFixed(2)}
                                </strong>{' '}
                                point(s) (Ratio: {pointRatio || 'N/A'} per $1)
                            </p>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="addVisit"
                                    checked={addVisit}
                                    onChange={(e) => setAddVisit(e.target.checked)}
                                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                />
                                <label htmlFor="addVisit" className="text-sm text-gray-700">
                                    Count +1 visit
                                </label>
                            </div>

                            <button
                                onClick={handleAddPointsClick}
                                className={`px-4 py-2 rounded transition-colors ${
                                    loading ||
                                    !moneySpent ||
                                    isNaN(parseFloat(moneySpent)) ||
                                    parseFloat(moneySpent) <= 0
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' // Disabled style
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                                disabled={
                                    loading ||
                                    !moneySpent ||
                                    isNaN(parseFloat(moneySpent)) ||
                                    parseFloat(moneySpent) <= 0
                                }
                            >
                                Add Points
                            </button>
                        </div>
                    )}
                        </>
                    )}
                </div>

                {/* Right Panel: Display Only */}
                <div className="w-full lg:w-1/2 bg-white shadow-md rounded-xl p-6 space-y-4">
                    <h2 className="text-2xl font-bold text-primary">Customer Pass Info</h2>

                    {customerData ? (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <p>
                                    <strong>Email:</strong> {customerEmail}
                                </p>
                                <p>
                                    <strong>Name:</strong> {customerName || 'N/A'}
                                </p>
                                <p>
                                    <strong>Phone:</strong> {customerData.phone || 'N/A'}
                                </p>
                            </div>

                            {/* Multiple passes list removed; showing compact Points & Visits below */}

                            {/* Selected Pass Points & Visits (compact visual) */}
                            {selectedPass &&
                                (() => {
                                    const sel = selectedPass;
                                    const points =
                                        typeof sel.points === 'number'
                                            ? sel.points
                                            : parseFloat(sel.points || 0);
                                    const visits =
                                        typeof sel.visits === 'number'
                                            ? sel.visits
                                            : parseInt(sel.visits || 0, 10);
                                    return (
                                        <div className="p-4 bg-blue-50 rounded-lg border">
                                            <h3 className="font-semibold text-gray-800 mb-3">
                                                Pass Summary
                                            </h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="flex flex-col items-center justify-center p-4 bg-white rounded shadow">
                                                    <p className="text-sm text-gray-500">Points</p>
                                                    <p className="text-3xl font-bold text-blue-600">
                                                        {points.toFixed(2)}
                                                    </p>
                                                    <p className="text-xs text-gray-400">
                                                        Ratio: {pointRatio || 'N/A'} pts/$
                                                    </p>
                                                </div>
                                                <div className="flex flex-col items-center justify-center p-4 bg-white rounded shadow">
                                                    <p className="text-sm text-gray-500">Visits</p>
                                                    <p className="text-3xl font-bold text-green-600">
                                                        {visits}
                                                    </p>
                                                    <p className="text-xs text-gray-400">
                                                        Counted visits for this pass
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                        </div>
                    ) : (
                        <p className="text-gray-500">No customer selected.</p>
                    )}
                    {rewardsLoading ? (
                        <div className="mt-6 flex flex-col items-center justify-center py-8 gap-3">
                            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm text-gray-600">Getting available rewards</p>
                        </div>
                    ) : availableRewards?.length > 0 ? (
                        <div className="mt-6">
                            <h3 className="text-xl font-semibold text-green-700 mb-2">
                                🎁 Available Rewards
                            </h3>
                            <ul className="space-y-4">
                                {availableRewards.map((reward) => (
                                    <li key={reward.id} className="border rounded p-3 bg-green-50">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="font-bold">{reward.title}</p>
                                                <p className="text-sm text-gray-700">
                                                    {'-' + reward.cost + ' ' + reward.type ||
                                                        'No description'}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => handleRedeemReward(reward.id)}
                                                className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                                            >
                                                Redeem
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        customerData && (
                            <p className="text-sm text-gray-500 mt-4">
                                No rewards currently available.
                            </p>
                        )
                    )}
                </div>
            </div>

            {/* Add points confirmation modal */}
            {showAddPointsConfirm && selectedPass && (
                <div
                    className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4"
                    onClick={() => setShowAddPointsConfirm(false)}
                >
                    <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Add points?</h3>
                        <p className="text-gray-600 text-sm mb-4">
                            Add{' '}
                            <strong>
                                {((parseFloat(moneySpent) || 0) * (pointRatio || 1)).toFixed(2)}
                            </strong>{' '}
                            point(s) {addVisit ? 'and +1 visit ' : ''}
                            {customerName ? `to ${customerName}'s pass` : "to this customer's pass"}?
                        </p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowAddPointsConfirm(false)}
                                className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddPointsConfirm}
                                disabled={loading}
                                className="flex-1 py-2 px-4 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50"
                            >
                                Add Points
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
